import type { Block, ProcessGroup, SwimlaneData } from '../types';

export type ParsedSheetPayload = {
  blocks: Block[];
  groups: ProcessGroup[];
  swimlaneData: SwimlaneData;
};

type CacheEntry = {
  signature: string;
  payload: ParsedSheetPayload;
};

const cache = new Map<string, CacheEntry>();

function sheetSignature(swim: string, second: string, line: string): string {
  return `${swim.length}:${second.length}:${line.length}:${swim.slice(0, 64)}:${line.slice(0, 64)}`;
}

export function parseSheetPayload(
  swim: string,
  second: string,
  line: string,
  processDisplayName: string
): ParsedSheetPayload | null {
  try {
    const blocks = JSON.parse(swim) as Block[];
    const groups = JSON.parse(second) as ProcessGroup[];
    const parsedLineData = JSON.parse(line) as SwimlaneData;

    const swimlaneData: SwimlaneData = {
      ...(parsedLineData || {
        width: 1200,
        height: 600,
        lines: [],
        header: { text: 'Swimlane Diagram', height: 40 },
      }),
      header: {
        ...((parsedLineData && parsedLineData.header) || {}),
        text: processDisplayName,
      },
    };

    return { blocks, groups, swimlaneData };
  } catch {
    return null;
  }
}

export function getParsedSheet(
  mapId: string,
  swim: string,
  second: string,
  line: string,
  processDisplayName: string
): ParsedSheetPayload | null {
  if (!mapId || !swim || !line) return null;

  const signature = sheetSignature(swim, second, line);
  const hit = cache.get(mapId);
  if (hit && hit.signature === signature) {
    return hit.payload;
  }

  const parsed = parseSheetPayload(swim, second, line, processDisplayName);
  if (!parsed) return null;

  cache.set(mapId, { signature, payload: parsed });
  return parsed;
}

/** Pré-parse les feuilles en arrière-plan (idle) pour accélérer le switch d'onglet. */
export function warmParsedSheetCache(
  sheets: Array<{
    _id?: string;
    id?: string;
    swimlane?: string;
    second?: string;
    line?: string;
    name?: string;
    process?: { title?: string; name?: string };
  }>
): void {
  const run = () => {
    for (const sheet of sheets) {
      const mapId = String(sheet._id ?? sheet.id ?? '');
      if (!mapId || !sheet.swimlane || !sheet.line) continue;
      const title =
        sheet.process?.title ||
        sheet.process?.name ||
        sheet.name ||
        'Swimlane Diagram';
      getParsedSheet(
        mapId,
        sheet.swimlane,
        sheet.second || '[]',
        sheet.line,
        title
      );
    }
  };

  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(run, { timeout: 4000 });
  } else {
    window.setTimeout(run, 100);
  }
}

export function invalidateParsedSheetCache(mapId: string): void {
  cache.delete(mapId);
}

export function clearParsedSheetCache(): void {
  cache.clear();
}
